import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { format } from 'date-fns';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PersonIcon from '@mui/icons-material/PersonOutlineRounded';
import { useState } from 'react';
import ListItemSkeleton from './ListItemSkeleton';
import { PaymentWithTransfers } from '../../types/payments';
import { formatDollarAmount } from '../../utils/global/helper';
import TransactionBadge from './TransactionBadge';
import {
  getGroupedTransfers,
  getPaymentSplitsFromPayments,
} from '../../helpers/payments';
import useUser from '../../hooks/global/useUser';
import { LoadedUser } from '../../interfaces';

interface Props {
  payments: PaymentWithTransfers[];
  isLoading?: boolean;
  useSpecStyle?: boolean;
}

function TransfersDetails({ payments, isLoading, useSpecStyle }: Props) {
  const theme = useTheme();
  const { user } = useUser<LoadedUser>();
  // sort by updatedAt
  const transfers = payments
    .flatMap(payment => payment.transfers ?? [])
    .sort(
      (a, b) =>
        new Date(a!.updatedAt).getTime() - new Date(b!.updatedAt).getTime(),
    );

  const groupedTransfers = getGroupedTransfers(payments);

  const paymentsSplit = getPaymentSplitsFromPayments(payments);
  const [showDetailed, setShowDetailed] = useState(false);

  const displayedTransfers = showDetailed ? transfers : groupedTransfers;

  const getTransferName = (transfer: (typeof transfers)[0]) =>
    transfer.entity === 'EMPLOYEE'
      ? user?.business?.employees?.find(
          e => e.stripeId === transfer.stripeAccountId,
        )?.displayName ?? 'artist'
      : 'shop';

  const getTooltip = (status: (typeof transfers)[0]['status']) => {
    if (status === 'CREATED') return 'Transfer has been sent.';
    if (status === 'REVERSED') return 'Transfer has been reversed.';
    if (status === 'CANCELED') return 'Transfer has been canceled.';
    return 'Transfer is pending until payments account is set up.';
  };

  return (
    <Stack spacing={0} width="100%" gap={2}>
      {!isLoading && (
        <>
          {paymentsSplit.map(payment => (
            <Box
              key={`${payment.id}-${payment.type}`}
              display="flex"
              justifyContent="space-between">
              <Typography
                variant={useSpecStyle ? undefined : 'body2'}
                sx={
                  useSpecStyle
                    ? {
                        fontSize: 'var(--sidebar-font-sm)',
                        color: 'var(--sidebar-secondary)',
                      }
                    : undefined
                }
                color={useSpecStyle ? undefined : 'text.secondary'}>
                {formatDollarAmount(payment.amount)} {payment.type}
              </Typography>
              <Typography
                variant={useSpecStyle ? undefined : 'body2'}
                sx={
                  useSpecStyle
                    ? {
                        fontSize: 'var(--sidebar-font-sm)',
                        color: 'var(--sidebar-ink)',
                      }
                    : undefined
                }
                color={useSpecStyle ? undefined : 'black'}>
                {formatDollarAmount(payment.transfersAmount)} net of fees
              </Typography>
            </Box>
          ))}
          {displayedTransfers.map(transfer => {
            const transferDate = new Date(transfer.updatedAt);
            const Icon =
              transfer.entity === 'EMPLOYEE' ? PersonIcon : StorefrontIcon;
            return (
              <Box
                key={transfer.id}
                display="flex"
                gap={0.75}
                alignItems="center"
                justifyContent="space-between">
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  alignSelf="start"
                  sx={
                    useSpecStyle
                      ? {
                          width: 28,
                          height: 28,
                          border: '1px solid var(--sidebar-border-light)',
                          borderRadius: '5px',
                        }
                      : { aspectRatio: '1/1' }
                  }
                  p={useSpecStyle ? 0 : 0.75}
                  borderRadius={useSpecStyle ? undefined : '5px'}
                  bgcolor={
                    useSpecStyle ? undefined : theme.palette.lightGrey.main
                  }>
                  <Icon
                    width={useSpecStyle ? 14 : 22}
                    height={useSpecStyle ? 14 : 22}
                  />
                </Box>

                <Stack flex={1}>
                  <Typography
                    variant={useSpecStyle ? undefined : 'h5'}
                    sx={
                      useSpecStyle
                        ? {
                            fontSize: 'var(--sidebar-font-base)',
                            fontWeight: 700,
                            color: 'var(--sidebar-ink)',
                          }
                        : undefined
                    }>
                    {formatDollarAmount(transfer.amount / 100)} sent to{' '}
                    {getTransferName(transfer)}
                  </Typography>
                  <Typography
                    variant={useSpecStyle ? undefined : 'body2'}
                    sx={
                      useSpecStyle
                        ? {
                            fontSize: 'var(--sidebar-font-xs)',
                            color: 'var(--sidebar-secondary)',
                          }
                        : undefined
                    }>
                    {format(transferDate, 'MMM d, yyyy')} at{' '}
                    {format(transferDate, 'h:mm a')}
                  </Typography>
                </Stack>

                <TransactionBadge
                  light={false}
                  status={transfer.status}
                  tooltipText={getTooltip(transfer.status)}
                />
              </Box>
            );
          })}
          {!groupedTransfers.length && (
            <Typography variant="body2" textAlign="center">
              No transfers yet. Transfers are processed automatically and may
              take a minute to appear.
            </Typography>
          )}
          {transfers.length > groupedTransfers.length && (
            <Button
              variant="text"
              size="small"
              startIcon={
                <ExpandMoreIcon
                  sx={{
                    transform: showDetailed ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.2s',
                  }}
                />
              }
              sx={{
                alignSelf: 'flex-end',
                '& .MuiButton-startIcon': { mr: 0.5 },
              }}
              onClick={() => setShowDetailed(prev => !prev)}>
              {showDetailed ? 'Show summarized view' : 'Show detailed view'}
            </Button>
          )}
        </>
      )}
      {isLoading && <ListItemSkeleton />}
    </Stack>
  );
}

export default TransfersDetails;
